<template>
  <van-cell v-if="!hidden || !field.hidden" class="field-container">
    <div v-if="field.label != undefined" class="name" :class="checkRequired && field.label && 'required'">{{ field.label }}</div>
    <template v-if="field.elType === 'select'">
      <van-field
        v-model="formatValue"
        class="field-select common-field"
        readonly
        :name="field.name"
        :placeholder="field.placeholder || $t(`${i18nPrefix}.placeholder.select`)"
        :disabled="field.disabled"
        :rules="field.rules"
        @click="handleShowSelect(field)"
      >
        <template #right-icon>
          <van-icon name="play" class="select-icon" />
        </template>
      </van-field>
      <van-popup
        v-model="showSelect"
        round
        position="bottom"
        class="select-popup"
        @close="handlePopupClose(field)"
      >
        <div class="popup-header">
          <van-icon class="popup-icon" name="cross" @click="handleCloseSelect(field)"/>
          <div class="popup-title">{{ field.popupLabel || field.label }}</div>
        </div>
        <div class="search-container">
          <van-cell
            v-if="!field.notSearch"
            value="search"
            class="keyword-input"
          >
          <template #default>
            <van-field
              v-model="keyword"
              :placeholder="$t(`${i18nPrefix}.placeholder.input`)"
            />
          </template>
          <template #icon>
            <van-icon name="search" class="search-icon" />
          </template>
          </van-cell>
        </div>
        <div class="selected-container"></div>
        <div class="popup-body">
          <template v-if="field.lazyLoad && !keyword">
            <van-cell>
              {{ $t('admin.application.applicantList.label.pleaseStartTyping') }}
            </van-cell>
          </template>
          <template v-else>
            <van-cell
              v-for="(option, index) in filterOptions"
              :key="index"
              :value="option.label"
              :value-class="hasSelected(option) && 'selected'"
              @click="handleSelect(field, option)"
            >
            <template #right-icon>
              <van-icon v-if="hasSelected(option)" class="success-icon" name="success" />
            </template>
            </van-cell>
          </template>
        </div>
        <div class="popup-footer">
          <div class="btn-group">
            <van-button v-if="!checkRequired" class="btn" :class="field.needConfirm ? 'reset-btn' : 'only-reset-btn'" @click="resetData(field)">{{ $t(`${i18nPrefix}.btn.clear`) }}</van-button>
            <van-button v-if="field.needConfirm" class="btn confirm-btn" :class="checkRequired && 'only-confirm-btn'" @click="confirmSelect(field)">{{ $t(`${i18nPrefix}.btn.confirm`) }}</van-button>
          </div>
        </div>
      </van-popup>
    </template>
    <template v-else-if="field.elType === 'calendar'">
      <van-field
        v-model="data[field.name]"
        class="field-calendar common-field"
        v-bind="field"
        readonly
        :name="field.name"
        :placeholder="field.placeholder || $t(`${i18nPrefix}.placeholder.selectDate`)"
        :disabled="field.disabled"
        :rules="field.rules"
        @click="handleShowCalendar(field)"
      />
      <van-calendar
        v-model="showCalendar"
        class="calendar-popup"
        :defaultDate="currentDate"
        :title="field.label"
        :min-date="field.minDate || minDate"
        :max-date="field.maxDate"
        :default-date="field.defaultDate || field.maxDate || maxDate"
        @confirm="onConfirmCalendar($event, field)"
        @close="onCloseCalendar($event)"
      />
    </template>
    <template v-else-if="field.elType === 'date'">
      <van-field
        v-model="data[field.name]"
        v-bind="field"
        class="data-field common-field"
        readonly
        :name="field.name"
        :placeholder="field.placeholder || $t(`${i18nPrefix}.placeholder.selectDate`)"
        :disabled="field.disabled"
        :rules="field.rules"
        @click="handleShowDate(field)"
      >
        <template #right-icon>
          <van-icon name="play" class="select-icon" />
        </template>
      </van-field>
      <van-popup
        v-model="showCalendar"
        round
        class="date-popup"
        position="bottom"
      >
        <van-datetime-picker
          v-model="currentDate"
          :confirm-button-text="$t('common.btn.confirm')"
          :cancel-button-text="$t('common.btn.cancel')"
          :type="field.dateType || 'year-month'"
          :title="field.dateTitle"
          :min-date="field.minDate || minDate"
          :max-date="field.maxDate"
          @confirm="handleConformDate(field)"
          @cancel="handleCancelDate(field)"
        />
        <div class="btn-container">
          <van-button v-if="!checkRequired" class="reset-btn" @click="resetData(field)">{{ $t(`${i18nPrefix}.btn.clear`) }}</van-button>
        </div>
      </van-popup>
    </template>
    <template v-else-if="field.elType === 'text'">
      <van-field
        v-model="data[field.name]"
        class="common-field textarea-field"
        type="textarea"
        clearable
        :autosize="field.autosize || true"
        rows="1"
        :name="field.name"
        :maxlength="field.maxLength || null"
        :disabled="field.disabled"
        :placeholder="field.placeholder"
        :rules="field.rules"
        @focus="onClick($event)"
        @blur="onBlur($event)"
      />
    </template>
    <template v-else-if="field.elType === 'question-text'">
      <van-field
        v-model="data[field.name]"
        class="common-field question-text"
        type="textarea"
        clearable
        :autosize="field.autosize || true"
        :rows="field.rows || '2'"
        :name="field.name"
        :disabled="field.disabled"
        :placeholder="field.placeholder"
        :rules="field.rules"
        @focus="onClick($event)"
        @blur="onBlur($event)"
      />
    </template>
    <template v-else-if="field.elType === 'radio'">
      <van-field class="radio-field" :rules="field.rules">
        <template #input>
          <slot :name="field.optionSlot || field.radioSlot" :line="{field: field}">
            <van-radio-group
              v-model="data[field.name]"
              icon-size=""
              :disabled="field.disabledAll"
              :direction="field.direction || 'horizontal'"
            >
              <van-radio
                v-for="(item, index) in field.options.data"
                :name="field.options.valueKey ? item[field.options.valueKey] : item.value"
                :key="index"
                :shape="item.shape"
                :disabled="item.disabled"
              >
                {{ field.options.labelKey? item[field.options.labelKey] : item.label }}
              </van-radio>
            </van-radio-group>
          </slot>
        </template>
      </van-field>
    </template>
    <template v-else-if="field.elType === 'checkbox'">
      <van-field class="checkbox-field" :rules="field.rules">
        <template #input>
          <slot :name="field.optionSlot" :line="{field: field}">
            <van-checkbox-group
              v-model="data[field.name]"
              icon-size=""
              :direction="field.direction"
              :disabled="field.disabledAll"
            >
              <van-checkbox
                v-for="(item, index) in field.options.data"
                :key="index"
                :name="field.options.valueKey ? item[field.options.valueKey] : item.value"
                :shape="item.shape || 'square'"
                :disabled="item.disabled"
              >
                {{ field.options.labelKey? item[field.options.labelKey] : item.label }}
              </van-checkbox>
            </van-checkbox-group>
          </slot>
        </template>
      </van-field>
    </template>
    <template v-else-if="field.elType === 'input' || !field.elType">
      <van-field
        class="common-field"
        v-bind="field"
        v-on="field.events"
        v-model="data[field.name]"
        :name="field.name"
        :placeholder="field.placeholder"
        :rules="field.rules"
        @focus="onClick($event)"
        @blur="onBlur($event)"
      >
        <template #right-icon>
          <slot :name="field.rightIcon"></slot>
        </template>
        <template #button>
          <slot :name="field.rightButton"></slot>
        </template>
      </van-field>
    </template>
    <slot :name="field.deleteButton"></slot>
  </van-cell>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

export default {
  components: {
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    groupIndex: {
      type: Number
    },
    groupName: {
      type: String
    },
    hidden: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      keyword: null,
      selectValue: null,
      isNotInOptions: false,
      selected: [],
      selectOptions: [],
      currentDate: new Date(),
      showCalendar: false,
      showSelect: false,
      closeSelect: true,
      minDate: new Date(1960, 0, 1),
      maxDate: new Date()
    }
  },
  computed: {
    formatValue: {
      get () {
        let value = this.selected
        if (value) {
          if (!Array.isArray(value)) {
            value = [value]
          }
          if (_.isEmpty(value)) {
            return this.selectValue
          }
          if (this.field.formatter) {
            return this.field.formatter(value)
          }
          const labelList = []
          _.forEach(value, item => {
            const labelKey = this.field.options?.labelKey || 'value'
            const option = _.find(this.selectOptions, [labelKey, item]) || _.find(this.selectOptions, ['id', item])
            if (option) {
              labelList.push(option.label)
            }
          })
          if (labelList.length > 0) {
            return labelList.join(',')
          }
        }
        return this.selectValue
      },
      set (value) {
        this.selected = value
      }
    },
    filterOptions () {
      const options = this.selectOptions
      if (this.field.lazyLoad) {
        if (!this.keyword) {
          return []
        }
      }
      if (options && this.keyword) {
        const filterOptions = _.filter(options, option => {
          return option.label.toLowerCase().includes(this.keyword.toLowerCase())
        })
        if (filterOptions.length === 0 && this.field.canNotInOptions) {
          let label = this.keyword
          if (this.field.options?.notInOptionsLabelFormatter) {
            label = this.field.options.notInOptionsLabelFormatter(this.keyword)
          }
          filterOptions.push({
            isNotInOptions: true,
            label: label,
            value: this.keyword
          })
        }
        return filterOptions
      }
      return options
    },
    checkRequired () {
      if (this.field.rules) {
        const required = _.find(this.field.rules, 'required')
        if (required) {
          return true
        }
      }
      return false
    }
  },
  created () {
  },
  mounted () {
  },
  methods: {
    initData () {
      if (this.field.elType === 'select') {
        if (this.data) {
          this.selected = this.data[this.field.name] || []
          this.selectValue = this.data[this.field.selectValueName]
          const options = this.field.options
          this.selectOptions = options.data || []
        }
      }
    },
    handleShowSelect (field) {
      if (field.disabled) {
        return
      }
      this.showSelect = true
      this.keyword = null
      this.initData()

      this.$emit('handleShowSelect', field)
    },
    handleCloseSelect (field) {
      // 取消此次操作
      this.showSelect = false
      this.$emit('handleCloseSelect', field)
    },
    handlePopupClose (field) {
      if (field.needConfirm || this.closeSelect) {
        this.selected = this.data[field.name] || []
      } else {
        this.confirmSelect(field)
      }
      this.closeSelect = true
    },
    handleSelect (field, option) {
      const value = option.value || option.id || option.labelKey
      if (field.multiple) {
        const index = _.indexOf(this.selected, value)
        if (index >= 0) {
          this.selected.splice(index, 1)
        } else {
          this.selected.push(value)
        }
      } else {
        this.selected = value
      }
      if (!field.needConfirm) {
        this.closeSelect = false
        this.showSelect = false
      }
    },
    resetSelect (field) {
      this.selected = null
      this.selectValue = null
    },
    confirmSelect (field) {
      // 允许输入的值不在options中
      if (field.canNotInOptions && this.filterOptions[0]?.isNotInOptions) {
        let selected = null
        if (field.options?.notInOptionsHandler) {
          selected = field.options.notInOptionsHandler(field, this.keyword)
        }
        this.selected = selected
        this.data[field.name] = selected
        this.selectValue = this.keyword
      } else {
        this.data[field.name] = this.selected
      }
      if (field.handler) {
        field.handler(field, this.selected, this.selectValue)
      }
      this.showSelect = false
    },
    hasSelected (option) {
      const labelKey = option.value || option.id || option.labelKey
      if (this.field.multiple) {
        return this.selected.includes(labelKey)
      } else {
        return this.selected === labelKey
      }
    },
    handleShowDate (field) {
      this.showCalendar = true
      this.currentDate = this.data[field.name] ? new Date(this.data[field.name]) : (field.defaultDate || field.maxDate || this.maxDate)
      this.$emit('handleShowDate', field)
    },
    handleCancelDate (field) {
      this.showCalendar = false
      this.$emit('handleCancelDate', field)
    },
    resetData (field) {
      this.showCalendar = false
      this.showSelect = false
      this.data[field.name] = null
    },
    handleConformDate (field) {
      let formatDate = ''
      if (field.formatter) {
        formatDate = field.formatter(this.currentDate)
      } else {
        formatDate = moment(this.currentDate).format('YYYY-MM-DD')
      }
      if (field.before) {
        const beforeDataKey = field.before.name
        const beforeDate = beforeDataKey ? this.data[beforeDataKey] : field.before.date
        if (beforeDate && new Date(beforeDate) < new Date(this.currentDate)) {
          if (beforeDataKey) {
            this.$toast({ message: this.$t(`${this.i18nPrefix}.message.resetEndDate`) })
            this.data[beforeDataKey] = ''
          } else {
            this.$toast({ message: this.$t(`${this.i18nPrefix}.message.beforeData`, { beforeDate: formatDate }), position: 'bottom' })
            return
          }
        }
      }
      if (field.after) {
        const afterDate = field.after.name ? this.data[field.after.name] : field.after.date
        if (afterDate && new Date(afterDate) > this.currentDate) {
          this.$toast({ message: this.$t(`${this.i18nPrefix}.message.afterDate`, { afterDate: afterDate }), position: 'bottom' })
          return
        }
      }
      this.data[field.name] = formatDate
      this.showCalendar = false
    },
    handleShowCalendar (field) {
      this.showCalendar = true
      this.currentDate = this.data[field.name] ? new Date(this.data[field.name]) : (field.defaultDate || field.maxDate || this.maxDate)
      this.$emit('handleShowCalendar', field)
    },
    onConfirmCalendar (calendar, field) {
      this.showCalendar = false
      if (field.formatter) {
        this.data[field.name] = field.formatter(calendar)
      } else {
        this.data[field.name] = this.formatDate(calendar)
      }
      this.$emit('onConfirmCalendar', calendar, field)
    },
    onCloseCalendar () {
      this.$emit('onCloseCalendar')
    },
    formatDate (calendar) {
      return `${calendar.getFullYear()}-${calendar.getMonth() + 1}-${calendar.getDate()}`
    },
    onClick (event) {
      this.$emit('onClick', event)
    },
    onBlur (event) {
      this.$emit('onBlur', event)
    },
    handleChange (value, field) {
      this.data[field.name] = value
    }
  },
  watch: {
    data: {
      immediate: true,
      handler () {
        this.initData()
      }
    },
    field: {
      immediate: true,
      handler () {
        this.initData()
      }
    }
  }

}
</script>

<style lang="scss" scoped>

$borderColor: #DCDFE6;
// $borderTransition: border-color 0.2s cubic-bezier(0.65, 0.05, 0.36, 1);

.field-container.van-cell {
  padding: 0;
  line-height: 20px;

  &.van-cell {
    padding: 0;
  }
  .name {
    font-weight: 400;
    font-size: 12px;
    color: #909399;
    line-height: 16px;
    text-align: justify;
    height: 16px;
  }

  .common-field {
    padding: 0;
    border-bottom: 1px solid $borderColor;

    ::v-deep {
      .van-field__label {
        display: none;
      }

      .van-field__body {
        font-size: 14px;
        min-height: 36px;
      }

      .van-field__control {
        line-height: 20px;
        color: #606266;

        &:disabled {
          color: #d9d9d9;
        }
      }
    }
  }

  .date-popup {
    box-sizing: border-box;
    padding: 5px 14px 24px;

    .btn-container {
      margin-top: 10px;

      .reset-btn {
          height: 44px;
          border-radius: 12px;
          font-size: 16px;
          width: 100%;
          color: #333;
          background-color: #f2f2f2;
      }
    }

    ::v-deep {
      &.van-popup--round {
        border-radius: 16px 16px 0 0;

        .van-picker__toolbar {
          height: 44px;

          .van-picker__cancel {
            padding: 0 16px;
            font-size: 14px;
          }

          .van-picker__confirm {
            padding: 0 16px;
            font-size: 14px;
          }

          .van-picker__title {
            font-size: 16px;
            line-height: 20px;
          }
        }

        .van-picker-column {
          font-size: 16px;
        }
      }

      .van-picker__confirm {
        color: var(--custom-color, #0096cc);
      }

      .van-picker-column__item--selected {
        color: var(--custom-color, #0096cc);
      }
    }
  }

  .textarea-field {
    white-space: pre-line;

    ::v-deep {
      .van-field__control {
        padding: 8px 0;
      }
    }
  }

  .question-text {
    border-bottom: none;

    ::v-deep {
      .van-field__body {
        padding: 5px;
        border: 1px solid #DCDFE6;
        border-radius: 4px;
      }
    }
  }

  .calendar-popup {
    height: 75vh;

    ::v-deep {
      &.van-popup--round {
        border-radius: 16px 16px 0 0;

        .van-calendar {
          .van-calendar__header {
            .van-calendar__header-title {
              font-size: 16px;
              height: 44px;
              line-height: 44px;
            }

            .van-calendar__header-subtitle {
              font-size: 14px;
              height: 44px;
              line-height: 44px;
            }

            .van-calendar__weekday {
              font-size: 12px;
              line-height: 30px;
            }
          }

          .van-calendar__body{
            .van-calendar__month {
              .van-calendar__day {
                height: 64px;
                font-size: 16px;
              }
            }
          }

          .van-calendar__footer {
            padding: 0 16px;

            .van-calendar__confirm {
              height: 36px;
              margin: 8px 0;

              .van-button__content {
                .van-button__text {
                  font-size: 14px;
                }
              }
            }
          }
        }
      }

      .van-calendar__selected-day {
        background-color: var(--custom-color, #0096cc);
      }

      .van-calendar__confirm {
        background-color: var(--custom-color, #0096cc);
        border-color: var(--custom-color, #0096cc);
      }
    }
  }

  .radio-field {
    padding: 0;

    ::v-deep {
      .van-radio {
        align-items: flex-start;
        margin-bottom: 5px;

        &:last-child {
          margin-bottom: 0;
        }

        .van-radio__icon {
          position: relative;
          font-size: 14px;
          top: 2px;
        }

        .van-radio__label {
          margin-left: 8px;
          line-height: 20px;
        }

        .van-radio__icon--checked .van-icon {
          background-color: var(--custom-color, #0096cc);
          border-color: var(--custom-color, #0096cc);
        }
      }

      .van-radio--horizontal {
        margin-right: 30px;
      }
    }
  }

  .checkbox-field {
    padding: 0;

    .van-checkbox-group {
      .van-checkbox {
        margin-bottom: 5px;
      }
    }

    ::v-deep {
      .van-checkbox {
        align-items: flex-start;
        margin-bottom: 5px;

        &:last-child {
          margin-bottom: 0;
        }

        .van-checkbox__icon {
          position: relative;
          font-size: 14px;
          top: 2px;
        }

        .van-checkbox__icon--checked .van-icon {
          background-color: var(--custom-color, #0096cc);
          border-color: var(--custom-color, #0096cc);
        }
      }

      .van-checkbox--horizontal {
        margin-right: 30px;
      }

      .van-checkbox__label {
        margin-left: 8px;
        line-height: 20px;
      }
    }
  }

  .field-select {
    .select-icon {
      font-size: 12px;
      transform: rotate(90deg)
    }

    ::v-deep {
      .van-field__right-icon {
        color: #909399;
        margin: 0;
        padding: 0;
      }
    }
  }

  .select-popup {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    max-height: 75vh;
    padding: 5px 14px 24px;

    .popup-header {
      .popup-icon {
        position: absolute;
        top: 12px;
        font-size: 22px;
      }

      .popup-title {
        padding: 5px 20px;
        font-size: 18px;
        height: 26px;
        line-height: 26px;
        text-align: center;
      }
    }

    .search-container {
      margin: 5px 0;

      .keyword-input {
        background: #f5f5f5;
        border-radius: 12px;

        .van-field {
          padding: 0;
          background: #f5f5f5;
        }

        .search-icon {
          font-size: 18px;
          top: 4px;
          margin-right: 2px;
        }
      }
    }

    .selected-container {
      height: 10px;
      border-bottom: 1px solid #F5F7FA;
    }

    .popup-body {
      overflow: scroll;
      flex: 1;

      .selected {
        color: var(--custom-color, #0096cc);
      }

      .success-icon {
        top: 5px;
        color: var(--custom-color, #0096cc);
      }
    }

    .popup-footer {
      padding-top: 10px;

      .btn-group {
        display: flex;
        text-align: center;
        flex-wrap: wrap;
        align-items: center;
        width: 100%;

        .btn {
          height: 44px;
          border-radius: 12px;
          font-size: 16px;
        }

        .reset-btn {
          width: 30%;
          color: #333;
          background-color: #f2f2f2;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .only-reset-btn {
          width: 100%;
          color: #333;
          background-color: #f2f2f2;
        }

        .confirm-btn {
          width: 70%;
          color: white;
          background-color: var(--custom-color, #0096cc);
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        .only-confirm-btn {
          width: 100%;
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
        }
      }
    }
  }

  .data-field {
    .select-icon {
      font-size: 12px;
      transform: rotate(90deg)
    }

    ::v-deep {
      .van-field__right-icon {
        color: #909399;
        margin: 0;
        padding: 0;
      }
    }
  }

  .required {
    &::after {
      position: relative;
      left: 2px;
      color: #ea7f7f;
      font-size: 14px;
      content: '*';
    }
  }

  ::v-deep {
    .van-cell--required {
      &::before {
        display: none;
      }
    }

    .required {
      &::after {
        color: #ea7f7f;
      }
    }
  }

  .disabled {
    background-color: #F5F7FA;
  }
}
</style>
